import React, {
  RefObject,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  PaginationState as ReactTablePaginationState,
} from '@tanstack/react-table';
import isNil from 'lodash/isNil';
import MonthPicker from 'core/components/DateTimePicker/MonthPicker';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  maxBillingCodeSearchQueryLength,
} from 'core/constants';
import DataProvider, {
  DataContext,
} from 'core/context/data';
import Filters from 'core/Filters';
import compact from 'lodash/compact';
import {
  useFilterQueryWithDateRange,
  useMedia,
  useTableReset,
} from 'core/hooks';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import SearchIcon from 'core/icons/search';
import useStorageState from 'core/hooks/storage';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import {
  TempoFilterQueryDTO,
  TempoPatientsRequestDTO,
  TempoPatientsResponseDTO,
  TempoPatientRowDTO,
  TempoSummaryDTO,
} from 'dtos';
import {
  postApiTempoDashboardGetPatients,
} from 'endpoints';
import columns from 'pages/Dashboard/pages/Dashboard/Tempo/columns';
import TempoPatientDetailDialog from 'pages/Dashboard/pages/Dashboard/Tempo/Details/TempoPatientDetailDialog';
import SummaryCards from 'pages/Dashboard/pages/Dashboard/Tempo/SummaryCards';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import {
  DateRange,
} from 'types/dateRange';
import dayjs from 'utils/dayjs';

type OverviewProps = {
  tableInstanceRef: RefObject<VirtualTableInstance>;
  selectedPatient?: TempoPatientRowDTO | null;
  onSelectPatient: (patient: TempoPatientRowDTO) => void;
};

function Overview({
  tableInstanceRef,
  selectedPatient,
  onSelectPatient,
}: OverviewProps) {
  const { data, auxiliary, isLoading } = useContext(DataContext);
  const [summary, setSummary] = useState<TempoSummaryDTO | undefined>();
  const { canRequestMultimedia } = useEMRAccess();
  const { requestMedia } = useMedia();

  useEffect(() => {
    if (!isNil(auxiliary)) {
      setSummary(auxiliary as TempoSummaryDTO);
    }
  }, [auxiliary]);

  useEffect(() => {
    if (canRequestMultimedia && !isNil(data)){

      const userIds = compact((data ?? []).map(({ provider }) => provider?.userId));
      if (userIds.length > 0) {
        requestMedia(userIds, MediaOriginatorType.User);
      }

      const patientIds = compact((data ?? []).map(({ patient }) => patient?.patientId));
      if (patientIds.length > 0) {
        requestMedia(patientIds);
      }
    }
  }, [data, canRequestMultimedia, requestMedia]);

  return (
    <div className="relative flex flex-col flex-1 overflow-y-hidden text-gray-600">
      <div className="px-4 pb-3">
        <SummaryCards
          summary={summary}
          isLoading={isLoading}
        />
      </div>
      <div className="w-full flex-1 min-h-0">
        <Table<TempoPatientRowDTO>
          instanceRef={tableInstanceRef}
          columns={columns}
          getRowId={(row) => row.encounterId ?? 0}
          emptySlot='No Tempo patients found'
          loadingSlot="Loading Tempo patients..."
          classes={{
            header: '!justify-start px-4',
            outerRow: (row) => (
              `!w-[calc(100%-16px)] left-4 cursor-pointer ${
                !isNil(selectedPatient)
                  && row.original.encounterId === selectedPatient.encounterId
                  ? '!bg-blue-50 !border-blue-200' : ''
              }`
            ),
            row: '!justify-start',
            cell: '!py-2 !min-h-[56px]',
          }}
          onRowClick={(_table, row) => {
            const patient = row.original;
            if (!isNil(patient.patient?.patientId)) {
              onSelectPatient(patient);
            }
          }}
        />
      </div>
    </div>
  );
}

const defaultTempoDateRange: DateRange = {
  min: dayjs().startOf('month').toDate(),
  max: dayjs().endOf('month').toDate(),
};

export default function Tempo() {
  const {
    reset,
    tableInstanceRef,
  } = useTableReset();
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery({ storageKey: 'tempoSearchQuery' });
  const {
    filterQuery,
    setFilterQuery,
    filterInstanceRef,
    dateRange,
    setDateRange,
  } = useFilterQueryWithDateRange<TempoFilterQueryDTO>(
    'tempoDashboard',
    {
      uniqueKey: 'tempoDateRange',
      defaultDateRange: defaultTempoDateRange,
    },
    true,
  );
  const [selectedPatient, setSelectedPatient] = useState<TempoPatientRowDTO | null>(null);
  const [defaultPagination] = useStorageState<ReactTablePaginationState>('tempoPagination', {
    pageSize: 25,
    pageIndex: 0,
  });

  function onFilterUpdate() {
    setFilterQuery(filterInstanceRef.current?.getFilterQuery?.() ?? {});
    reset();
  }

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      <div className="flex flex-wrap items-center gap-3 px-4">
        <SearchInput
          placeholder="Search by patient name"
          className="w-80"
          delay={400}
          value={searchQuery}
          onChange={(value) => {
            setSearchQuery(value);
            reset();
          }}
          inputProps={{ autoComplete: 'off', maxLength: maxBillingCodeSearchQueryLength }}
          icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
          id="tempo-search"
        />
        <MonthPicker
          value={dayjs(dateRange.min)}
          placeholder="MM/YYYY"
          onChange={(value) => {
            if (!isNil(value)) {
              setDateRange({
                min: value.startOf('month').toDate(),
                max: value.endOf('month').toDate(),
              });
              reset();
            }
          }}
        />
        <Filters
          pageObject="tempoDashboard"
          onFilterUpdate={onFilterUpdate}
          instanceRef={filterInstanceRef}
          useStorage
        />
      </div>
      <DataProvider<
        TempoPatientsResponseDTO,
        TempoPatientRowDTO,
        TempoSummaryDTO
      >
        queryKey="tempo-patients"
        pagination={defaultPagination}
        getData={(request: TempoPatientsRequestDTO) => (
          postApiTempoDashboardGetPatients(request)
        )}
        getMeta={(response) => (response?.patients?.pageProperties ?? {})}
        getAuxiliary={(response) => response?.summary}
        getQuery={({ dateRange: queryDateRange, searchQuery: querySearch, filterQuery: queryFilter, pagination }) => ({
          dateRange: serializeDateRange(queryDateRange),
          searchText: querySearch,
          filterQuery: queryFilter,

          pageRequest: {
            page: pagination?.pageIndex ?? 0,
            pageSize: pagination?.pageSize ?? 25,
          },
        } satisfies TempoPatientsRequestDTO)}
        normalize={(response) => response?.patients?.data ?? []}
        params={{
          dateRange,
          searchQuery,
          filterQuery,
        }}
      >
        <Overview
          tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
          selectedPatient={selectedPatient}
          onSelectPatient={setSelectedPatient}
        />
      </DataProvider>
      {!isNil(selectedPatient) && (
        <TempoPatientDetailDialog
          patientData={selectedPatient}
          dateRange={dateRange}
          onClose={() => { setSelectedPatient(null); }}
        />
      )}
    </div>
  );
}
